import { useState, useEffect, useMemo } from "react";
import type { Job, DetailedPermitStage } from "@/data/types";
import { JobService } from "@/services/job.service";
import { FileUploader } from "@/components/ui/file-uploader";
import type { Attachment } from "@/data/attachmentTypes";
import { getAttachments, getDownloadUrl } from "@/services/attachment.service";
import { createSignedStorageFullImageUrl } from "@/lib/storageImageUrls";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  FileText, ExternalLink, CheckCircle2, AlertCircle, ChevronDown, ChevronUp,
  Upload, Link as LinkIcon, Phone, Mail, UserPlus, Send, Eye, Download,
} from "lucide-react";
import { toast } from "sonner";
import { useAppData } from "@/data/AppDataContext";
import { FilePreviewModal } from "@/components/ui/file-preview-modal";

/* ── Types ──────────────────────────────────────────── */

export interface PermitPrep {
  floorplanUrl?: string;
  floorplanSecondBathroomUrl?: string;
  selectedFormId?: string;
  formFilledUrl?: string;
  signatureRequest?: {
    signer: "contractor" | "homeowner";
    status: "pending" | "sent" | "executed" | "cancelled";
    sentTo?: string;
    docusignEnvelopeId?: string;
    executedAt?: string;
    completedFormUrl?: string;
  };
  customerCallStatus?: "pending" | "called" | "voicemail" | "no_answer";
  customerConfirmedEmail?: string;
  callScriptUsed?: string;
  routing?: {
    assignedTo: string;
    status: "pending" | "sent";
    sentAt?: string;
    notes?: string;
  };
  notarizationRequired?: boolean;
  notarizationComplete?: boolean;
}

/* ── Enums ──────────────────────────────────────────── */

export type HomeType = "SFH" | "CONDO" | "MOBILE" | "Manufactured" | "Other";
export type NtboStatus = "Not Started" | "Preparing" | "Submitted to Municipality" | "Approved" | "PDF Generated" | "Other";
export type PlumbingPermitStatus = "Not Started" | "In Progress" | "Completed" | "Items Needed" | "Other";

const HOME_TYPES: HomeType[] = ["SFH", "CONDO", "MOBILE", "Manufactured", "Other"];
const NTBO_STATUSES: NtboStatus[] = ["Not Started", "Preparing", "Submitted to Municipality", "Approved", "PDF Generated", "Other"];
const PLUMBING_STATUSES: PlumbingPermitStatus[] = ["Not Started", "In Progress", "Completed", "Items Needed", "Other"];
const SIGNER_OPTIONS = ["contractor", "homeowner"] as const;
const CALL_STATUS_OPTIONS = ["pending", "called", "voicemail", "no_answer"] as const;

const COUNTY_APPRAISER_LINKS: Record<string, string> = {
  "Hillsborough": "https://www.hcpafl.org/",
  "Orange": "https://www.occompt.com/",
  "Pinellas": "https://www.pcpao.org/",
  "Duval": "https://www.coj.net/departments/property-appraiser",
  "Broward": "https://www.browardapa.org/",
  "Miami-Dade": "https://www.miamidade.gov/pa/",
  "Palm Beach": "https://www.pbcgov.org/papa/",
  "Sarasota": "https://www.sc-pa.com/",
  "Lee": "https://www.lee-pa.com/",
  "Collier": "https://www.colliercountyfl.gov/your-government/divisions/property-appraiser/",
};

/* ── Helpers ────────────────────────────────────────── */

function getCountyFromCity(city: string | undefined): string | null {
  if (!city) return null;
  const lower = city.toLowerCase();
  const map: Record<string, string> = {
    tampa: "Hillsborough", orlando: "Orange", "st. petersburg": "Pinellas",
    jacksonville: "Duval", "fort lauderdale": "Broward", miami: "Miami-Dade",
    "west palm beach": "Palm Beach", sarasota: "Sarasota", "fort myers": "Lee",
    naples: "Collier",
  };
  for (const [key, county] of Object.entries(map)) {
    if (lower.includes(key)) return county;
  }
  return null;
}

function buildActivityMessage(changes: Record<string, string>): string {
  const parts = Object.entries(changes)
    .filter(([, v]) => v !== undefined && v !== null && v !== "")
    .map(([k, v]) => `${k} → ${v}`);
  return parts.length > 0 ? `NTBO tracker updated: ${parts.join(", ")}` : "NTBO tracker updated";
}

/* ── Component ──────────────────────────────────────── */

interface Props {
  job: Job;
  onSave?: (updatedJob: Job) => void;
  readOnly?: boolean;
}

export function NtboTrackerPanel({ job, onSave, readOnly = false }: Props) {
  const { setJobs } = useAppData();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showPrep, setShowPrep] = useState(false);
  const [ntboAttachments, setNtboAttachments] = useState<Attachment[]>([]);
  const [loadingAttachments, setLoadingAttachments] = useState(false);

  const currentStage = job.permitDetail?.detailedStage as DetailedPermitStage | undefined;
  const isPreparingDocuments = currentStage === "Preparing Documents";
  const prep = (job.permitDetail as { prep?: PermitPrep } | undefined)?.prep;

  const [form, setForm] = useState({
    homeType: job.homeType || "",
    parcelNumber: job.parcelNumber || "",
    ntboStatus: job.ntboStatus || "",
    plumbingPermitStatus: job.plumbingPermitStatus || "",
    permitExpectationDate: job.permitExpectationDate || "",
    homeownerAvailableDate: job.homeownerAvailableDate || "",
    drawingFloorPlanCompleted: job.drawingFloorPlanCompleted || false,
    secondBathroomDrawingCompleted: job.secondBathroomDrawingCompleted || false,
    readyForInspection: job.readyForInspection || false,
    inspectionConfirmed: job.inspectionConfirmed || false,
  });

  const [prepForm, setPrepForm] = useState<PermitPrep>({
    floorplanUrl: prep?.floorplanUrl || "",
    floorplanSecondBathroomUrl: prep?.floorplanSecondBathroomUrl || "",
    selectedFormId: prep?.selectedFormId || "",
    formFilledUrl: prep?.formFilledUrl || "",
    signatureRequest: prep?.signatureRequest || { signer: "contractor", status: "pending" },
    customerCallStatus: prep?.customerCallStatus || "pending",
    customerConfirmedEmail: prep?.customerConfirmedEmail || "",
    callScriptUsed: prep?.callScriptUsed || "",
    routing: prep?.routing || { assignedTo: "", status: "pending" },
    notarizationRequired: prep?.notarizationRequired || false,
    notarizationComplete: prep?.notarizationComplete || false,
  });

  const [uploadedFiles, setUploadedFiles] = useState<Attachment[]>([]);
  const [uploading, setUploading] = useState(false);
  const [previewUrls, setPreviewUrls] = useState<Record<string, string>>({});
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<Attachment | null>(null);

  const county = useMemo(() => getCountyFromCity(job.address?.city), [job.address?.city]);
  const appraiserLink = county ? COUNTY_APPRAISER_LINKS[county] : null;

  useEffect(() => {
    if (!job.id) return;
    setLoadingAttachments(true);
    getAttachments("job", job.id)
      .then((atts) => {
        const permitAtts = atts.filter(a => a.attachmentType === "permit");
        setNtboAttachments(permitAtts);
      })
      .catch(() => {})
      .finally(() => setLoadingAttachments(false));
  }, [job.id]);

  useEffect(() => {
    setForm({
      homeType: job.homeType || "",
      parcelNumber: job.parcelNumber || "",
      ntboStatus: job.ntboStatus || "",
      plumbingPermitStatus: job.plumbingPermitStatus || "",
      permitExpectationDate: job.permitExpectationDate || "",
      homeownerAvailableDate: job.homeownerAvailableDate || "",
      drawingFloorPlanCompleted: job.drawingFloorPlanCompleted || false,
      secondBathroomDrawingCompleted: job.secondBathroomDrawingCompleted || false,
      readyForInspection: job.readyForInspection || false,
      inspectionConfirmed: job.inspectionConfirmed || false,
    });
    setPrepForm({
      floorplanUrl: prep?.floorplanUrl || "",
      floorplanSecondBathroomUrl: prep?.floorplanSecondBathroomUrl || "",
      selectedFormId: prep?.selectedFormId || "",
      formFilledUrl: prep?.formFilledUrl || "",
      signatureRequest: prep?.signatureRequest || { signer: "contractor", status: "pending" },
      customerCallStatus: prep?.customerCallStatus || "pending",
      customerConfirmedEmail: prep?.customerConfirmedEmail || "",
      callScriptUsed: prep?.callScriptUsed || "",
      routing: prep?.routing || { assignedTo: "", status: "pending" },
      notarizationRequired: prep?.notarizationRequired || false,
      notarizationComplete: prep?.notarizationComplete || false,
    });
  }, [job, prep]);

  const handleFieldChange = (field: string, value: unknown) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handlePrepChange = (field: string, value: unknown) => {
    setPrepForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    const changes: Record<string, string> = {};

    const trackerPatch: Record<string, unknown> = {};
    if (form.homeType !== (job.homeType || "")) { trackerPatch.homeType = form.homeType || null; changes.home_type = form.homeType || "—"; }
    if (form.parcelNumber !== (job.parcelNumber || "")) { trackerPatch.parcelNumber = form.parcelNumber || null; changes.parcel_number = form.parcelNumber || "—"; }
    if (form.ntboStatus !== (job.ntboStatus || "")) { trackerPatch.ntboStatus = form.ntboStatus || null; changes.ntbo_status = form.ntboStatus || "—"; }
    if (form.plumbingPermitStatus !== (job.plumbingPermitStatus || "")) { trackerPatch.plumbingPermitStatus = form.plumbingPermitStatus || null; changes.plumbing_permit_status = form.plumbingPermitStatus || "—"; }
    if (form.permitExpectationDate !== (job.permitExpectationDate || "")) { trackerPatch.permitExpectationDate = form.permitExpectationDate || null; changes.permit_expectation_date = form.permitExpectationDate || "—"; }
    if (form.homeownerAvailableDate !== (job.homeownerAvailableDate || "")) { trackerPatch.homeownerAvailableDate = form.homeownerAvailableDate || null; changes.homeowner_available_date = form.homeownerAvailableDate || "—"; }
    if (form.drawingFloorPlanCompleted !== (job.drawingFloorPlanCompleted || false)) { trackerPatch.drawingFloorPlanCompleted = form.drawingFloorPlanCompleted; changes.drawing_floor_plan_completed = String(form.drawingFloorPlanCompleted); }
    if (form.secondBathroomDrawingCompleted !== (job.secondBathroomDrawingCompleted || false)) { trackerPatch.secondBathroomDrawingCompleted = form.secondBathroomDrawingCompleted; changes.second_bathroom_drawing_completed = String(form.secondBathroomDrawingCompleted); }
    if (form.readyForInspection !== (job.readyForInspection || false)) { trackerPatch.readyForInspection = form.readyForInspection; changes.ready_for_inspection = String(form.readyForInspection); }
    if (form.inspectionConfirmed !== (job.inspectionConfirmed || false)) { trackerPatch.inspectionConfirmed = form.inspectionConfirmed; changes.inspection_confirmed = String(form.inspectionConfirmed); }

    const prepChanged = JSON.stringify(prepForm) !== JSON.stringify(prep || {});
    const newSubStatus = isPreparingDocuments ? deriveNtboSubStatus(prepForm) : undefined;
    const currentSubStatus = (job.permitDetail as import("@/data/types").PermitDetail | undefined)?.ntboSubStatus;
    const currentAssignedTo = (job.permitDetail as import("@/data/types").PermitDetail | undefined)?.assignedTo;
    const newAssignedTo = isPreparingDocuments ? derivePermitAssignee(prepForm) : undefined;
    const subStatusChanged = newSubStatus !== currentSubStatus;
    const assignedToChanged = newAssignedTo !== currentAssignedTo;
    const prepPatch = (prepChanged || subStatusChanged || assignedToChanged)
      ? {
          permitDetail: {
            ...job.permitDetail,
            ...(prepChanged ? { prep: prepForm } : {}),
            ...(newSubStatus !== undefined ? { ntboSubStatus: newSubStatus } : {}),
            ...(newAssignedTo !== undefined ? { assignedTo: newAssignedTo } : {}),
          },
        }
      : {};

    if (Object.keys(trackerPatch).length === 0 && Object.keys(prepPatch).length === 0) {
      setSaving(false);
      setEditing(false);
      return;
    }

    const activityEntry = {
      id: `act-${Date.now()}`,
      date: new Date().toISOString(),
      user: "system",
      action: buildActivityMessage(changes),
      noteType: "ops" as const,
    };

    try {
      const { data, error } = await JobService.update(job.id, {
        ...trackerPatch,
        ...prepPatch,
        activityHistory: [activityEntry, ...(job.activityHistory || [])],
      });

      if (error || !data) {
        toast.error("Failed to save NTBO tracker");
        setSaving(false);
        return;
      }

      setJobs(prev => prev.map(j => j.id === job.id ? data : j));
      onSave?.(data);
      setEditing(false);
      toast.success("NTBO tracker updated");

      if (form.drawingFloorPlanCompleted && currentStage === "Permit Required") {
        toast.info("Drawings complete — consider advancing to Preparing Documents", {
          action: {
            label: "Advance",
            onClick: () => {
              JobService.update(job.id, { permitDetail: { ...job.permitDetail, detailedStage: "Preparing Documents" as DetailedPermitStage } });
            },
          },
        });
      }
    } catch {
      toast.error("Failed to save NTBO tracker");
    }
    setSaving(false);
  };

  const handleUploadComplete = async (attachments: Attachment[]) => {
    setUploadedFiles(prev => [...prev, ...attachments]);
    const names = attachments.map(a => a.fileName).join(", ");
    const activityEntry = {
      id: `act-${Date.now()}`,
      date: new Date().toISOString(),
      user: "system",
      action: `NTBO document uploaded: ${names}`,
      noteType: "ops" as const,
    };
    try {
      const { data, error } = await JobService.update(job.id, {
        activityHistory: [activityEntry, ...(job.activityHistory || [])],
      });
      if (data) {
        setJobs(prev => prev.map(j => j.id === job.id ? data : j));
        onSave?.(data);
      }
    } catch {
      // non-blocking
    }
  };

  const handlePreview = async (attachment: Attachment) => {
    setPreviewAttachment(attachment);
    setPreviewOpen(true);
  };

  const handleDownload = async (attachment: Attachment) => {
    setDownloadingId(attachment.id);
    try {
      const url = await getDownloadUrl(attachment.id, 3600);
      const a = document.createElement("a");
      a.href = url;
      a.download = attachment.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      toast.error("Failed to download file");
    } finally {
      setDownloadingId(null);
    }
  };

  const handleRouteToDebora = async () => {
    if (!prepForm.routing?.assignedTo) {
      toast.error("Enter recipient name before routing");
      return;
    }
    const updatedRouting = { ...prepForm.routing, status: "sent" as const, sentAt: new Date().toISOString() };
    handlePrepChange("routing", updatedRouting);
    const activityEntry = {
      id: `act-${Date.now()}`,
      date: new Date().toISOString(),
      user: "system",
      action: `NTBO permit packet routed to ${updatedRouting.assignedTo}`,
      noteType: "ops" as const,
    };
    try {
      const { data, error } = await JobService.update(job.id, {
        permitDetail: { ...job.permitDetail, prep: { ...prepForm, routing: updatedRouting } },
        activityHistory: [activityEntry, ...(job.activityHistory || [])],
      });
      if (data) {
        setJobs(prev => prev.map(j => j.id === job.id ? data : j));
        onSave?.(data);
        toast.success(`Routed to ${updatedRouting.assignedTo}`);
      }
    } catch {
      toast.error("Failed to route packet");
    }
  };

  const renderField = (
    label: string,
    field: keyof typeof form,
    input: React.ReactNode,
  ) => (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</Label>
      {input}
    </div>
  );

  const renderPrepField = (
    label: string,
    field: keyof PermitPrep,
    input: React.ReactNode,
  ) => (
    <div className="space-y-1.5">
      <Label className="text-[11px] font-medium text-muted-foreground uppercase tracking-wider">{label}</Label>
      {input}
    </div>
  );

  return (
    <Card className="shadow-none border">
      <CardContent className="p-4 space-y-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 flex-wrap">
            <FileText className="h-4 w-4 text-primary" />
            <span className="text-sm font-bold">Notice to Building Official (NTBO)</span>
            {currentStage && (
              <Badge variant="outline" className="text-[10px]">{currentStage}</Badge>
            )}
            {isPreparingDocuments && (() => {
              const sub = deriveNtboSubStatus(prepForm);
              const assignee = derivePermitAssignee(prepForm);
              return (
                <>
                  {sub !== "NotStarted" && (
                    <Badge variant="outline" className={`text-[10px] ${NTBO_SUB_STATUS_COLOR[sub]}`}>
                      {NTBO_SUB_STATUS_LABEL[sub]}
                    </Badge>
                  )}
                  <Badge variant="outline" className={`text-[10px] ${ASSIGNEE_COLOR[assignee]}`}>
                    {assignee}
                  </Badge>
                </>
              );
            })()}
          </div>
          <div className="flex items-center gap-2">
            {!readOnly && !editing && (
              <Button variant="outline" size="sm" onClick={() => setEditing(true)}>Edit</Button>
            )}
            {editing && (
              <>
                <Button variant="ghost" size="sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
                <Button size="sm" onClick={handleSave} disabled={saving}>
                  {saving ? "Saving..." : "Save"}
                </Button>
              </>
            )}
          </div>
        </div>

        <Separator />

        {/* ── Tracker Fields ── */}
        {editing ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {renderField("Home Type", "homeType",
              <Select value={form.homeType} onValueChange={v => handleFieldChange("homeType", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {HOME_TYPES.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {renderField("Parcel Number", "parcelNumber",
              <Input value={form.parcelNumber} onChange={e => handleFieldChange("parcelNumber", e.target.value)} placeholder="APN / Parcel #" className="h-8 text-xs" />
            )}
            {renderField("NTBO Status", "ntboStatus",
              <Select value={form.ntboStatus} onValueChange={v => handleFieldChange("ntboStatus", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {NTBO_STATUSES.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {renderField("Plumbing Permit Status", "plumbingPermitStatus",
              <Select value={form.plumbingPermitStatus} onValueChange={v => handleFieldChange("plumbingPermitStatus", v)}>
                <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select..." /></SelectTrigger>
                <SelectContent>
                  {PLUMBING_STATUSES.map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            {renderField("Permit Expectation Date", "permitExpectationDate",
              <Input type="date" value={form.permitExpectationDate} onChange={e => handleFieldChange("permitExpectationDate", e.target.value)} className="h-8 text-xs" />
            )}
            {renderField("Homeowner Available Date", "homeownerAvailableDate",
              <Input type="date" value={form.homeownerAvailableDate} onChange={e => handleFieldChange("homeownerAvailableDate", e.target.value)} className="h-8 text-xs" />
            )}
            <div className="flex items-center gap-2 pt-5">
              <Checkbox id="drawingFloorPlanCompleted" checked={form.drawingFloorPlanCompleted} onCheckedChange={v => handleFieldChange("drawingFloorPlanCompleted", v)} />
              <Label htmlFor="drawingFloorPlanCompleted" className="text-xs cursor-pointer">Floor Plan Completed</Label>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox id="secondBathroomDrawingCompleted" checked={form.secondBathroomDrawingCompleted} onCheckedChange={v => handleFieldChange("secondBathroomDrawingCompleted", v)} />
              <Label htmlFor="secondBathroomDrawingCompleted" className="text-xs cursor-pointer">Second Bathroom Drawing Completed</Label>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox id="readyForInspection" checked={form.readyForInspection} onCheckedChange={v => handleFieldChange("readyForInspection", v)} />
              <Label htmlFor="readyForInspection" className="text-xs cursor-pointer">Ready for Inspection</Label>
            </div>
            <div className="flex items-center gap-2 pt-5">
              <Checkbox id="inspectionConfirmed" checked={form.inspectionConfirmed} onCheckedChange={v => handleFieldChange("inspectionConfirmed", v)} />
              <Label htmlFor="inspectionConfirmed" className="text-xs cursor-pointer">Inspection Confirmed</Label>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
            {[
              { label: "Home Type", value: form.homeType || "—" },
              { label: "Parcel Number", value: form.parcelNumber || "—" },
              { label: "NTBO Status", value: form.ntboStatus || "—" },
              { label: "Plumbing Permit", value: form.plumbingPermitStatus || "—" },
              { label: "Permit Expectation", value: form.permitExpectationDate || "—" },
              { label: "Homeowner Available", value: form.homeownerAvailableDate || "—" },
            ].map(item => (
              <div key={item.label}>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                <p className="font-medium text-foreground truncate">{item.value}</p>
              </div>
            ))}
            {[
              { label: "Floor Plan", value: form.drawingFloorPlanCompleted ? "Completed" : "Pending", tone: form.drawingFloorPlanCompleted ? "text-success" : "text-warning" },
              { label: "2nd Bath Drawing", value: form.secondBathroomDrawingCompleted ? "Completed" : "Pending", tone: form.secondBathroomDrawingCompleted ? "text-success" : "text-warning" },
              { label: "Ready for Inspection", value: form.readyForInspection ? "Yes" : "No", tone: form.readyForInspection ? "text-success" : "text-destructive" },
              { label: "Inspection Confirmed", value: form.inspectionConfirmed ? "Yes" : "No", tone: form.inspectionConfirmed ? "text-success" : "text-destructive" },
            ].map(item => (
              <div key={item.label}>
                <p className="text-[10px] text-muted-foreground uppercase tracking-wider">{item.label}</p>
                <p className={`font-medium ${item.tone}`}>{item.value}</p>
              </div>
            ))}
          </div>
        )}

        <Separator />

        {/* ── Permit Prep Workflow (collapsible) ── */}
        <div className="space-y-2">
          <button
            type="button"
            onClick={() => setShowPrep(v => !v)}
            className="flex items-center gap-2 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
          >
            {showPrep ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            Permit Prep Workflow
            {isPreparingDocuments && <Badge variant="outline" className="text-[10px] h-5 px-1.5">Active Stage</Badge>}
          </button>

          {showPrep && (
            <div className="space-y-4 rounded-lg border bg-muted/20 p-3">
              {/* Floorplan Assets */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {renderPrepField("Floorplan URL", "floorplanUrl",
                  <div className="flex gap-2">
                    <Input value={prepForm.floorplanUrl} onChange={e => handlePrepChange("floorplanUrl", e.target.value)} placeholder="Canva / Polycam link" className="h-8 text-xs flex-1" disabled={!editing} />
                    {prepForm.floorplanUrl && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild><a href={prepForm.floorplanUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3" /></a></Button>}
                  </div>
                )}
                {renderPrepField("2nd Bathroom Floorplan", "floorplanSecondBathroomUrl",
                  <div className="flex gap-2">
                    <Input value={prepForm.floorplanSecondBathroomUrl} onChange={e => handlePrepChange("floorplanSecondBathroomUrl", e.target.value)} placeholder="Canva / Polycam link" className="h-8 text-xs flex-1" disabled={!editing} />
                    {prepForm.floorplanSecondBathroomUrl && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild><a href={prepForm.floorplanSecondBathroomUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3" /></a></Button>}
                  </div>
                )}
              </div>

              {/* Property Lookup Helper */}
              {appraiserLink && (
                <div className="flex items-center gap-2 text-xs">
                  <span className="text-muted-foreground">County Appraiser:</span>
                  <Button variant="link" size="sm" className="h-auto p-0 text-xs" asChild>
                    <a href={appraiserLink} target="_blank" rel="noreferrer">{county} Property Appraiser <ExternalLink className="h-3 w-3 ml-1" /></a>
                  </Button>
                </div>
              )}

              {/* Form Selection */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {renderPrepField("Form Template", "selectedFormId",
                  <Select value={prepForm.selectedFormId} onValueChange={v => handlePrepChange("selectedFormId", v)} disabled={!editing}>
                    <SelectTrigger className="h-8 text-xs"><SelectValue placeholder="Select form..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ntbo">NTBO — Notice to Building Official</SelectItem>
                      <SelectItem value="other">Other / Municipality-specific</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {renderPrepField("Filled Form URL", "formFilledUrl",
                  <div className="flex gap-2">
                    <Input value={prepForm.formFilledUrl} onChange={e => handlePrepChange("formFilledUrl", e.target.value)} placeholder="pdffiller link" className="h-8 text-xs flex-1" disabled={!editing} />
                    {prepForm.formFilledUrl && <Button variant="ghost" size="sm" className="h-8 w-8 p-0" asChild><a href={prepForm.formFilledUrl} target="_blank" rel="noreferrer"><ExternalLink className="h-3 w-3" /></a></Button>}
                  </div>
                )}
              </div>

              {/* Signature Workflow */}
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Signature Workflow</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Signer</Label>
                    <Select value={prepForm.signatureRequest?.signer} onValueChange={v => handlePrepChange("signatureRequest", { ...prepForm.signatureRequest, signer: v as "contractor" | "homeowner" })} disabled={!editing}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {SIGNER_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt === "contractor" ? "Contractor (Debora)" : "Homeowner"}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Signature Status</Label>
                    <Select
                      value={prepForm.signatureRequest?.status || "pending"}
                      onValueChange={(v) =>
                        setPrepForm(prev => ({
                          ...prev,
                          signatureRequest: { ...(prev.signatureRequest || { signer: "contractor" }), status: v as "pending" | "sent" | "executed" | "cancelled" },
                        }))
                      }
                      disabled={readOnly}
                    >
                      <SelectTrigger className="h-7 text-xs mt-0.5">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="executed">Executed</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Sent To</Label>
                    <Input value={prepForm.signatureRequest?.sentTo || ""} onChange={e => handlePrepChange("signatureRequest", { ...prepForm.signatureRequest, sentTo: e.target.value })} placeholder="email@example.com" className="h-8 text-xs" disabled={!editing} />
                  </div>
                </div>
                {/* Notarization required */}
                <div className="flex items-center gap-2 mt-1">
                  <Checkbox
                    id="notarization-required"
                    checked={!!prepForm.notarizationRequired}
                    onCheckedChange={(v) => handlePrepChange("notarizationRequired", !!v)}
                    disabled={readOnly}
                  />
                  <label htmlFor="notarization-required" className="text-xs text-muted-foreground cursor-pointer">
                    Notarization required
                  </label>
                </div>
                {prepForm.notarizationRequired && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="notarization-complete"
                      checked={!!prepForm.notarizationComplete}
                      onCheckedChange={(v) => handlePrepChange("notarizationComplete", !!v)}
                      disabled={readOnly}
                    />
                    <label htmlFor="notarization-complete" className="text-xs text-muted-foreground cursor-pointer">
                      Notarization complete
                    </label>
                  </div>
                )}
              </div>

              {/* Customer Contact */}
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Customer Contact</p>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Call Status</Label>
                    <Select value={prepForm.customerCallStatus} onValueChange={v => handlePrepChange("customerCallStatus", v)} disabled={!editing}>
                      <SelectTrigger className="h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {CALL_STATUS_OPTIONS.map(opt => <SelectItem key={opt} value={opt}>{opt.replace("_", " ")}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Confirmed Email</Label>
                    <Input value={prepForm.customerConfirmedEmail} onChange={e => handlePrepChange("customerConfirmedEmail", e.target.value)} placeholder="customer@email.com" className="h-8 text-xs" disabled={!editing} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Script Used</Label>
                    <Input value={prepForm.callScriptUsed} onChange={e => handlePrepChange("callScriptUsed", e.target.value)} placeholder="Script reference..." className="h-8 text-xs" disabled={!editing} />
                  </div>
                </div>
              </div>

              {/* Internal Routing */}
              <div className="space-y-2">
                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">Internal Routing</p>
                <div className="flex items-end gap-2">
                  <div className="flex-1 space-y-1.5">
                    <Label className="text-[10px] text-muted-foreground">Assign To</Label>
                    <Input value={prepForm.routing?.assignedTo || ""} onChange={e => handlePrepChange("routing", { ...prepForm.routing, assignedTo: e.target.value })} placeholder="Name or role..." className="h-8 text-xs" disabled={!editing} />
                  </div>
                  <Button size="sm" variant="secondary" onClick={handleRouteToDebora} disabled={!editing} className="h-8">
                    <Send className="h-3 w-3 mr-1.5" /> Route
                  </Button>
                </div>
                {prepForm.routing?.sentAt && (
                  <p className="text-[10px] text-muted-foreground">
                    Sent on {new Date(prepForm.routing.sentAt).toLocaleString()} to {prepForm.routing.assignedTo}
                  </p>
                )}
              </div>
            </div>
          )}
        </div>

        <Separator />

        {/* ── Document Upload ── */}
        <div className="space-y-2">
          <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wider">NTBO Documents</p>
          <FileUploader
            parentType="job"
            parentId={job.id}
            attachmentType="permit"
            acceptedTypes={['application/pdf', 'image/jpeg', 'image/png']}
            maxFiles={10}
            maxSizeMB={20}
            onUploadComplete={handleUploadComplete}
            className="[&_.border-dashed]:p-3"
            disabled={readOnly}
          />
          {(uploadedFiles.length > 0 || ntboAttachments.length > 0) && (
            <div className="space-y-1">
              {[...uploadedFiles, ...ntboAttachments].map(a => (
                <div key={a.id} className="flex items-center gap-2 text-xs text-muted-foreground">
                  <FileText className="h-3 w-3 shrink-0" />
                  <span className="truncate flex-1">{a.fileName}</span>
                  {a.fileSize && <span className="text-[10px]">({(a.fileSize / 1024).toFixed(0)} KB)</span>}
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] px-1.5"
                    onClick={() => handlePreview(a)}
                    title="Preview"
                  >
                    <Eye className="h-3 w-3" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-6 text-[10px] px-1.5"
                    onClick={() => handleDownload(a)}
                    disabled={downloadingId === a.id}
                    title="Download"
                  >
                    <Download className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </CardContent>
      <FilePreviewModal
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        attachments={previewAttachment ? [previewAttachment] : []}
      />
    </Card>
  );
}

/* ── Exported derivation utilities (used by PermittingPortal) ── */

export const NTBO_SUB_STATUS_LABEL: Record<import("@/data/types").NtboSubStatus, string> = {
  NotStarted: "Not Started",
  AwaitingHomeownerSignature: "Awaiting Signature",
  AwaitingNotarization: "Awaiting Notarization",
  ReadyForSubmission: "Ready for Submission",
};

export const NTBO_SUB_STATUS_COLOR: Record<import("@/data/types").NtboSubStatus, string> = {
  NotStarted: "bg-muted text-muted-foreground border-border",
  AwaitingHomeownerSignature: "bg-warning/15 text-warning border-warning/30",
  AwaitingNotarization: "bg-info/15 text-info border-info/30",
  ReadyForSubmission: "bg-success/15 text-success border-success/30",
};

export function deriveNtboSubStatus(prep: PermitPrep | undefined): import("@/data/types").NtboSubStatus {
  if (!prep) return "NotStarted";
  const sigStatus = prep.signatureRequest?.status;
  if (sigStatus === "sent") return "AwaitingHomeownerSignature";
  if (sigStatus === "executed") {
    if (prep.notarizationRequired && !prep.notarizationComplete) return "AwaitingNotarization";
    return "ReadyForSubmission";
  }
  return "NotStarted";
}

export const PERMIT_ASSIGNEES: import("@/data/types").PermitAssignee[] = ["Abby", "Debora", "Wesley", "Homeowner"];

export const ASSIGNEE_COLOR: Record<import("@/data/types").PermitAssignee, string> = {
  Abby:      "bg-info/15 text-info border-info/30",
  Debora:    "bg-success/15 text-success border-success/30",
  Wesley:    "bg-primary/15 text-primary border-primary/30",
  Homeowner: "bg-warning/15 text-warning border-warning/30",
};

export function derivePermitAssignee(prep: PermitPrep | undefined): import("@/data/types").PermitAssignee {
  const sub = deriveNtboSubStatus(prep);
  switch (sub) {
    case "AwaitingHomeownerSignature": return "Homeowner";
    case "AwaitingNotarization":       return "Wesley";
    case "ReadyForSubmission":         return "Debora";
    default:                           return "Abby";
  }
}
